import { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { v4 as uuidv4 } from "uuid";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const SIDEBAR_FIELDS = [
  { type: "name", label: "Name" },
  { type: "signature", label: "Signature" },
  { type: "date", label: "Date" },
];

const PdfTemplateEditor = ({
  file,
  onSave,
  onClose,
  existingFields = null,
}) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [fields, setFields] = useState([]);
  const [draggingField, setDraggingField] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const pdfContainerRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();

    const rect = pdfContainerRef.current.getBoundingClientRect();

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setFields((prev) => [
      ...prev,
      {
        id: uuidv4(),
        type: draggingField.type,
        x,
        y,
        page: currentPage,
      },
    ]);

    setDraggingField(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSave = () => {
    onSave(fields);
  };

  useEffect(() => {
  if (!existingFields) return;

  const converted = existingFields.map((field) => ({
    id: field.id || uuidv4(),
    type: field.field,
    x: field.x,
    y: field.y,
    page: field.page || 1,
  }));

  setFields(converted);
}, [existingFields]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center">
      <div className="w-[95%] h-[95%] bg-slate-950 rounded-xl flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-64 border-r border-white/10 p-4 space-y-3">
          <h2 className="text-lg font-semibold">Fields</h2>

          {SIDEBAR_FIELDS.map((item) => (
            <div
              key={item.type}
              draggable
              onDragStart={() => setDraggingField(item)}
              className="cursor-move bg-pink-600/20 border border-pink-500 text-pink-400 px-3 py-2 rounded-md text-sm hover:bg-pink-600/30 transition"
            >
              {item.label}
            </div>
          ))}
        </div>

        {/* Main Area */}
        <div className="flex-1 relative overflow-auto">
          {/* Top bar */}
          <div className="flex ml-20 gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-white/20 text-slate-300 hover:bg-slate-800 transition"
            >
              Cancel
            </button>

            <button
              onClick={handleSave}
              className="bg-pink-600 hover:bg-pink-700 px-4 py-2 rounded-lg text-sm text-white font-medium transition"
            >
              Save Template
            </button>
          </div>

          {/* PDF Container */}
          <div className="flex-1 overflow-auto flex justify-center items-start p-10">
            <div
              ref={pdfContainerRef}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onMouseMove={(e) => {
                if (!activeId) return;

                const rect = pdfContainerRef.current.getBoundingClientRect();

                let x = e.clientX - rect.left;
                let y = e.clientY - rect.top;

                // Clamp inside bounds
                x = Math.max(0, Math.min(x, rect.width - 80));
                y = Math.max(0, Math.min(y, rect.height - 30));

                setFields((prev) =>
                  prev.map((f) => (f.id === activeId ? { ...f, x, y } : f)),
                );
              }}
              onMouseUp={() => setActiveId(null)}
              className="relative inline-block"
            >
              <Document
                file={file}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              >
                {/* <Page
                  pageNumber={1}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                /> */}
                {Array.from(new Array(numPages), (el, index) => (
                  <div key={index} className="relative">
                    <Page
                      pageNumber={index + 1}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onLoadSuccess={() => setCurrentPage(index + 1)}
                    />
                  </div>
                ))}
              </Document>

              {/* Overlay Fields */}
              {fields.map((field) => (
                <div
                  key={field.id}
                  onMouseDown={(e) => {
                    setActiveId(field.id);
                    e.stopPropagation();
                  }}
                  className="absolute bg-pink-600 text-white text-xs px-3 py-1.5 rounded-md cursor-move select-none shadow-lg"
                  style={{
                    left: field.x,
                    top: field.y,
                  }}
                >
                  {field.type}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfTemplateEditor;
